import { importJWK, CompactEncrypt } from 'jose';
import { instance as axiosInstance } from '../api/index'; // Use the shared axios instance

// This will hold the imported public key for the duration of the session.
let publicKey = null;
const PUBLIC_KEY_ENDPOINT = '/api/crypto/public-key';
const SECURE_CONTEXT_ERROR_MESSAGE =
    'Encryption requires HTTPS or localhost. LAN HTTP addresses cannot import the public key in this browser.';

const assertWebCryptoSupport = () => {
    if (window.isSecureContext && window.crypto?.subtle) return;

    throw new Error(SECURE_CONTEXT_ERROR_MESSAGE);
};

const resolvePublicKeyJwk = (responseData) => {
    if (responseData?.kty) return responseData;
    if (responseData?.publicKey?.kty) return responseData.publicKey;
    if (responseData?.data?.kty) return responseData.data;
    if (responseData?.data?.publicKey?.kty) return responseData.data.publicKey;

    throw new Error('Public key response did not include a valid JWK.');
};

/**
 * Initializes the encryption service by fetching the public key from the backend.
 * This function MUST be called once when the application starts.
 */
export async function initEncryptionService() {
    try {
        assertWebCryptoSupport();
        console.log('Fetching public key for encryption...');
        const response = await axiosInstance.get(PUBLIC_KEY_ENDPOINT);
        const jwk = resolvePublicKeyJwk(response.data);

        // Import the JWK (JSON Web Key) to a CryptoKey object that 'jose' can use.
        publicKey = await importJWK(jwk, 'RSA-OAEP-256');
        console.log('✅ Encryption service initialized successfully.');
    } catch (error) {
        console.error('❌ CRITICAL: Failed to fetch or import public key. Encryption will not work.', error);
        // In a real app, you might want to show a global error message to the user.
        throw new Error('Could not initialize encryption service.');
    }
}

/**
 * Encrypts a JSON payload using the server's public key.
 * @param {object} payload - The JavaScript object to encrypt.
 * @returns {Promise<string>} - The JWE (JSON Web Encryption) compact string.
 */
export async function encryptPayload(payload) {
    if (!publicKey) {
        console.error('Encryption service has not been initialized. Cannot encrypt payload.');
        throw new Error('Encryption service not ready.');
    }

    try {
        // 1. Convert the payload object to a JSON string.
        const jsonString = JSON.stringify(payload);
        // 2. Encode the string into a byte array (Uint8Array).
        const encodedPayload = new TextEncoder().encode(jsonString);

        // 3. Create a JWE using the CompactEncrypt class.
        const jwe = await new CompactEncrypt(encodedPayload)
            .setProtectedHeader({ alg: 'RSA-OAEP-256', enc: 'A256GCM' }) // These MUST match the backend's expected algorithms.
            .encrypt(publicKey);

        return jwe;
    } catch (error) {
        console.error('Failed to encrypt payload:', error);
        throw error;
    }
}
